import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';

/* What happens to a task when the note is added. Deliberately the same
   control as the compose box's status picker: one primary button does the
   thing, and any state change is *staged* next to it rather than offered as
   a second, equally-loud button you can hit by mistake. */

export type TaskAction = 'none' | 'complete' | 'reopen';

const MENU_WIDTH = 240;

interface Props {
  /** Current task state — decides which options are offered. */
  status: 'open' | 'completed' | string;
  staged: TaskAction;
  onSelect: (action: TaskAction) => void;
  onClear: () => void;
  disabled?: boolean;
}

const OPEN_TONE = 'var(--scpa-status-warning)';
const DONE_TONE = 'var(--scpa-status-ok)';

export default function TaskActionPicker({ status, staged, onSelect, onClear, disabled }: Props) {
  const { open, setOpen, triggerRef, menuRef, style } = useAnchoredMenu({ width: MENU_WIDTH, align: 'left' });
  const finished = status === 'completed';

  const options: Array<{ value: TaskAction; label: string; tone: string; hint?: string }> = finished
    ? [
        { value: 'none', label: 'Leave completed', tone: DONE_TONE },
        { value: 'reopen', label: 'Reopen the task', tone: OPEN_TONE },
      ]
    : [
        { value: 'none', label: 'Leave open', tone: OPEN_TONE },
        { value: 'complete', label: 'Mark complete', tone: DONE_TONE, hint: 'needs a note' },
      ];

  const currentLabel = finished ? 'Completed' : 'Open';
  const currentTone = finished ? DONE_TONE : OPEN_TONE;
  const stagedOption = staged === 'none' ? null : options.find(o => o.value === staged) ?? null;

  const pick = (value: TaskAction) => {
    setOpen(false);
    onSelect(value);
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        className="btn btn-ghost text-xs py-1.5 gap-1.5"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="What happens to this task when you add the note"
      >
        <span className="text-muted font-medium">Task:</span>
        <span style={{ color: currentTone, fontWeight: 700 }}>{currentLabel}</span>
        {stagedOption && (
          <>
            <span className="text-muted">→</span>
            <span style={{ color: stagedOption.tone, fontWeight: 700 }}>
              {staged === 'complete' ? 'Completed' : 'Open'}
            </span>
          </>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {stagedOption && (
        <button
          type="button"
          onClick={onClear}
          className="ml-1 text-muted hover:text-err"
          title="Leave the task as it is"
          aria-label="Clear staged task change"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      {open && style && createPortal(
        <div ref={menuRef} role="listbox" className="bg-surface border border-line rounded-lg shadow-xl overflow-y-auto" style={style}>
          <div className="py-1">
            {options.map(o => {
              const isSelected = o.value === staged;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => pick(o.value)}
                  className="w-full px-3 py-2 text-sm text-left hover:bg-row-odd flex items-center gap-2"
                >
                  <span className="w-4 flex items-center justify-center shrink-0">
                    {isSelected && <Check className="w-3.5 h-3.5 text-primary" />}
                  </span>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: o.tone }} />
                  <span className="text-fg truncate">{o.label}</span>
                  {o.value === 'none' && <span className="ml-auto text-[11px] text-muted shrink-0">current</span>}
                  {o.hint && <span className="ml-auto text-[11px] text-muted shrink-0">{o.hint}</span>}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
